import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useActiveOrganization } from "./useActiveOrganization";
import { useAuth } from "./useAuth";
import { ModulePermissions, PERMISSION_TEMPLATES } from "@/types/permissions";
import { useOrganizationMembers } from "./useOrganizationMembers";

interface MemberPermission {
  id: string;
  member_id: string;
  permissions: Partial<ModulePermissions>;
  created_at: string;
  updated_at: string;
}

export const useMemberPermissions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  const { user } = useAuth();
  const { members } = useOrganizationMembers(activeOrganizationId || undefined);

  // Encontrar o member_id do usuário atual
  const currentMember = members.find((m) => m.user_id === user?.id);

  // Buscar as permissões customizadas do usuário atual
  const { data: currentUserPermissions, isLoading } = useQuery({
    queryKey: ["member-permissions", currentMember?.id],
    queryFn: async () => {
      if (!currentMember?.id) return null;

      const { data, error } = await supabase
        .from("member_permissions")
        .select("*")
        .eq("member_id", currentMember.id)
        .maybeSingle();

      if (error) throw error;

      return data as MemberPermission | null;
    },
    enabled: !!currentMember?.id,
  });

  // Buscar todas as permissões dos membros (para admins)
  const { data: allMemberPermissions = [] } = useQuery({
    queryKey: ["all-member-permissions", activeOrganizationId],
    queryFn: async () => {
      if (!activeOrganizationId) return [];

      const { data, error } = await supabase
        .from("member_permissions")
        .select("*, organization_members!inner(organization_id)")
        .eq("organization_members.organization_id", activeOrganizationId);

      if (error) throw error;

      return data as MemberPermission[];
    },
    enabled: !!activeOrganizationId,
  });

  // Função para obter permissões de um membro específico
  const getPermissionsForMember = (memberId: string): Partial<ModulePermissions> => {
    const member = members.find((m) => m.id === memberId);
    const customPermissions = allMemberPermissions.find((p) => p.member_id === memberId);

    if (customPermissions?.permissions) {
      return customPermissions.permissions;
    }

    // Se não houver permissões customizadas, usar template do role
    if (member?.role) {
      return PERMISSION_TEMPLATES[member.role] || {};
    }

    return {};
  };

  // Obter permissões do usuário atual (custom ou template)
  const getCurrentPermissions = (): Partial<ModulePermissions> => {
    if (currentUserPermissions?.permissions) {
      return currentUserPermissions.permissions;
    }

    // Se não houver permissões customizadas, usar template do role
    if (currentMember?.role) {
      return PERMISSION_TEMPLATES[currentMember.role] || {};
    }

    return {};
  };

  // Verificar se tem uma permissão específica
  const hasPermission = (module: keyof ModulePermissions, permission: string): boolean => {
    const permissions = getCurrentPermissions();
    const modulePermissions = permissions[module] as any;
    return modulePermissions?.[permission] === true;
  };

  // Mutation para atualizar permissões de um membro
  const updatePermissionsMutation = useMutation({
    mutationFn: async ({
      memberId,
      permissions,
    }: {
      memberId: string;
      permissions: Partial<ModulePermissions>;
    }) => {
      const { data: existing } = await supabase
        .from("member_permissions")
        .select("id")
        .eq("member_id", memberId)
        .maybeSingle();

      if (existing) {
        // Atualizar
        const { error } = await supabase
          .from("member_permissions")
          .update({ permissions })
          .eq("member_id", memberId);

        if (error) throw error;
      } else {
        // Inserir
        const { error } = await supabase
          .from("member_permissions")
          .insert({ member_id: memberId, permissions });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["all-member-permissions"] });
      toast({
        title: "Permissões atualizadas",
        description: "As permissões do usuário foram atualizadas com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar permissões",
        description: error.message,
      });
    },
  });

  // Aplicar template de permissões
  const applyTemplateMutation = useMutation({
    mutationFn: async ({
      memberId,
      template,
    }: {
      memberId: string;
      template: keyof typeof PERMISSION_TEMPLATES;
    }) => {
      const permissions = PERMISSION_TEMPLATES[template];
      
      const { data: existing } = await supabase
        .from("member_permissions")
        .select("id")
        .eq("member_id", memberId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("member_permissions")
          .update({ permissions })
          .eq("member_id", memberId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("member_permissions")
          .insert({ member_id: memberId, permissions });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["all-member-permissions"] });
      toast({
        title: "Template aplicado",
        description: "O template de permissões foi aplicado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao aplicar template",
        description: error.message,
      });
    },
  });

  return {
    currentPermissions: getCurrentPermissions(),
    isLoading,
    hasPermission,
    getPermissionsForMember,
    updatePermissions: updatePermissionsMutation.mutate,
    applyTemplate: applyTemplateMutation.mutate,
    isUpdating: updatePermissionsMutation.isPending || applyTemplateMutation.isPending,
  };
};
